import { createContext, type ReactNode, useContext } from 'react';
import type { ApiClient } from './ApiClient';

/**
 * React Context for providing ApiClient throughout the component tree
 */
const ApiClientContext = createContext<ApiClient | null>(null);

/**
 * Provider component that makes ApiClient available to all child components
 *
 * @example
 * ```tsx
 * const apiClient = createApiClient(axiosInstance);
 *
 * <ApiClientProvider client={apiClient}>
 *   <App />
 * </ApiClientProvider>
 * ```
 */
export function ApiClientProvider({
  client,
  children,
}: {
  client: ApiClient;
  children: ReactNode;
}) {
  if (!client) {
    throw new Error('ApiClientProvider requires a valid ApiClient instance');
  }

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Hook to access the ApiClient instance from React components
 *
 * @throws Error if used outside of ApiClientProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const apiClient = useApiClient();
 *
 *   const fetchData = async () => {
 *     const data = await apiClient.get('/endpoint');
 *   };
 * }
 * ```
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);

  if (!client) {
    throw new Error(
      'useApiClient must be used within an ApiClientProvider. ' +
        'Make sure your component tree is wrapped with <ApiClientProvider client={apiClient}>.'
    );
  }

  return client;
}
